/**
 * kioskAuth.js — shared helpers for the venue kiosk pages.
 *
 * The PIN Login page stores a short-lived JWT in localStorage plus
 * a shorthand staff object. Terminal + Kitchen pages call `useKiosk`
 * on mount to redirect to the PIN pad if the token is missing or has
 * expired, and to expose the currently signed-in staff row.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export const VENUE_PIN_TOKEN_KEY = "venue_pin_token";
export const VENUE_PIN_STAFF_KEY = "venue_pin_staff";
export const VENUE_PIN_EXP_KEY   = "venue_pin_exp";

export function readKioskStaff() {
  try {
    const raw = localStorage.getItem(VENUE_PIN_STAFF_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function kioskAuthHeader() {
  const tok = localStorage.getItem(VENUE_PIN_TOKEN_KEY)
           || localStorage.getItem("token")
           || localStorage.getItem("access_token");
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}

export function kioskExpired() {
  const exp = parseInt(localStorage.getItem(VENUE_PIN_EXP_KEY) || "0", 10);
  return !exp || Date.now() > exp;
}

export function kioskSignOut() {
  localStorage.removeItem(VENUE_PIN_TOKEN_KEY);
  localStorage.removeItem(VENUE_PIN_STAFF_KEY);
  localStorage.removeItem(VENUE_PIN_EXP_KEY);
  // Leave `token` alone — a real user may be signed in via /auth/login.
}

export function useKiosk(vendorId, opts = {}) {
  const nav = useNavigate();
  const [staff, setStaff] = useState(readKioskStaff());
  useEffect(() => {
    if (!localStorage.getItem(VENUE_PIN_TOKEN_KEY) || kioskExpired()) {
      kioskSignOut();
      // Build the "next" pointer from the CURRENT URL, but never nest
      // it: if we're already on a login page (with or without its own
      // ?next=), unwrap it so the redirect chain is at most one hop.
      const path   = window.location.pathname;
      const search = window.location.search;
      let next     = path + search;
      if (path.endsWith("/login")) {
        const sp   = new URLSearchParams(search);
        const inner= sp.get("next");
        next = inner && !inner.endsWith("/login") ? inner : "";
      }
      const target = next
        ? `/venue-terminal/${vendorId}/login?next=${encodeURIComponent(next)}`
        : `/venue-terminal/${vendorId}/login`;
      nav(target, { replace: true });
      return;
    }
    setStaff(readKioskStaff());
  }, [vendorId, nav]);
  return { staff, signOut: () => {
    kioskSignOut();
    nav(`/venue-terminal/${vendorId}/login`, { replace: true });
  }};
}
